/**
 * Token Status Component
 * Displays JWT token information for debugging and user awareness
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getTokenTimeToExpiry } from '../utils/jwt';

const TokenStatus = ({ showDetails = false }) => {
  const { getTokenInfo, isAuthenticated } = useAuth();
  const [timeToExpiry, setTimeToExpiry] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const updateTimeToExpiry = () => {
      const timeLeft = getTokenTimeToExpiry();
      setTimeToExpiry(timeLeft);
    };

    // Update immediately
    updateTimeToExpiry();

    // Update every minute
    const interval = setInterval(updateTimeToExpiry, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated]);

  if (!isAuthenticated) {
    return null;
  }

  const tokenInfo = getTokenInfo();
  if (!tokenInfo) {
    return null;
  }

  const formatTime = (seconds) => {
    if (seconds <= 0) return 'Expired';
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const isExpiringSoon = timeToExpiry < 300; // Less than 5 minutes

  return (
    <div className="token-status">
      {isExpiringSoon && (
        <div className="token-warning">
          <strong>⚠️ Session expires in {formatTime(timeToExpiry)}</strong>
        </div>
      )}
      
      {showDetails && (
        <details className="token-details">
          <summary>Token Information</summary>
          <div className="token-info">
            <p><strong>Expires in:</strong> {formatTime(timeToExpiry)}</p>
            <p><strong>Issued at:</strong> {tokenInfo.payload?.iat ? new Date(tokenInfo.payload.iat * 1000).toLocaleString() : 'N/A'}</p>
            <p><strong>Expires at:</strong> {tokenInfo.payload?.exp ? new Date(tokenInfo.payload.exp * 1000).toLocaleString() : 'N/A'}</p>
            {tokenInfo.payload?.iss && (
              <p><strong>Issuer:</strong> {tokenInfo.payload.iss}</p>
            )}
          </div>
        </details>
      )}
    </div>
  );
};

export default TokenStatus;
